'use client';
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlaskConical, Phone, ArrowLeft, ChevronRight, AlertCircle, Shield, Lock, MessageCircle, Download, Eye, Info, User, Loader2, FileText, Calendar, MapPin, Building2,  } from 'lucide-react';
import { useRouter } from 'next/navigation';
import TopNav from '@/components/layout/TopNav';
import { hospitalConfig } from '@/data/hospitalConfig';
import {
  PathologyService,
  DEMO_MOBILE_NUMBER,
  DEMO_OTP,
  buildReportWhatsAppMessage,
  generateLabRequestId,
  type PatientRecord,
  type LabReport,
} from '@/data/pathologyService';

// ─── Flow Steps ───────────────────────────────────────────────────────────────
type LabStep =
  | 'landing' |'enter-mobile' |'enter-otp' |'reports-list' |'report-viewer' |'whatsapp-simulation';

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: LabReport['status'] }) {
  const map = {
    available: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50',
    pending: 'bg-amber-900/30 text-amber-400 border-amber-900/50',
    processing: 'bg-blue-900/30 text-blue-400 border-blue-900/50',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold tracking-wider uppercase ${map[status]}`}>
      {status}
    </span>
  );
}

// ─── Demo Badge ───────────────────────────────────────────────────────────────
function DemoBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-950/50 border border-amber-900/50 text-amber-400 text-xs font-semibold tracking-wider">
      DEMO
    </span>
  );
}

// ─── OTP Input ────────────────────────────────────────────────────────────────
function OtpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.split('').concat(Array(6).fill('')).slice(0, 6);

  const handleChange = (idx: number, char: string) => {
    const d = char.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = d;
    onChange(next.join(''));
    if (d && idx < 5) inputs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          className="w-11 h-14 text-center text-xl font-bold text-white bg-white/5 border border-white/15 rounded-xl focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
          aria-label={`OTP digit ${i + 1}`}
        />
      ))}
    </div>
  );
}

// ─── WhatsApp Icon ────────────────────────────────────────────────────────────
function WhatsAppIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LabReportContent() {
  const router = useRouter();
  const [step, setStep] = useState<LabStep>('landing');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [patient, setPatient] = useState<PatientRecord | null>(null);
  const [reports, setReports] = useState<LabReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<LabReport | null>(null);
  const [requestId, setRequestId] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  // ── Landing ──────────────────────────────────────────────────────────────
  const LandingStep = () => (
    <motion.div
      key="landing"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <div className="mb-8">
        <p className="section-label mb-2">DIAGNOSTICS — LAB REPORTS</p>
        <h1 className="text-display-md text-white font-bold leading-tight mb-3">
          GET MY<br />
          <span className="gold-gradient-text">LAB REPORT.</span>
        </h1>
        <p className="text-muted-foreground text-sm max-w-md leading-relaxed">
          Access your latest available lab report securely through WhatsApp.
        </p>
      </div>

      {/* Demo notice */}
      <div className="mb-6 p-4 rounded-xl bg-amber-950/40 border border-amber-900/50 flex items-start gap-3">
        <Info size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs text-amber-400 font-semibold mb-1">PROTOTYPE — DEMO MODE</p>
          <p className="text-xs text-amber-400/70 leading-relaxed">
            This is a demonstration of the future All Is Well lab report workflow. No real patient data is used. Use the demo credentials below to explore the experience.
          </p>
          <div className="mt-2 p-2 rounded-lg bg-amber-950/60 border border-amber-900/40">
            <p className="text-xs text-amber-300 font-mono">Demo Mobile: {DEMO_MOBILE_NUMBER}</p>
            <p className="text-xs text-amber-300 font-mono">Demo OTP: {DEMO_OTP}</p>
          </div>
        </div>
      </div>

      {/* Flow preview */}
      <div className="mb-8 p-5 rounded-2xl bg-card border border-white/8">
        <p className="text-xs font-semibold tracking-widest text-white/30 uppercase mb-4">HOW IT WORKS</p>
        <div className="space-y-3">
          {[
            { icon: Phone, label: 'Enter your registered mobile number' },
            { icon: Shield, label: 'Verify with a secure OTP' },
            { icon: FlaskConical, label: 'View your available lab reports' },
            { icon: MessageCircle, label: 'Receive your report on WhatsApp' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <item.icon size={13} className="text-primary/70" />
              </div>
              <p className="text-sm text-white/60">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Security note */}
      <div className="mb-6 p-3 rounded-xl bg-white/3 border border-white/8 flex items-start gap-2">
        <Lock size={12} className="text-white/30 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-white/30 leading-relaxed">
          Your lab reports contain sensitive medical information. We verify your identity before providing access. Reports are never publicly accessible.
        </p>
      </div>

      <button
        onClick={() => setStep('enter-mobile')}
        className="w-full flex items-center justify-center gap-2 btn-primary py-4 text-sm tracking-widest"
        aria-label="Get my lab report"
      >
        GET REPORT ON WHATSAPP
        <ChevronRight size={16} />
      </button>
    </motion.div>
  );

  // ── Enter Mobile ─────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    const normalised = mobile.replace(/\s/g, '').replace(/^(\+91|91)/, '');
    if (!/^\d{10}$/.test(normalised)) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    setError('');
    setLoading(true);
    const result = await PathologyService.sendOtp(normalised);
    setLoading(false);
    if (result.success) {
      setOtpSent(true);
      setStep('enter-otp');
    } else {
      setError(result.error || 'Failed to send OTP. Please try again.');
    }
  };

  const EnterMobileStep = () => (
    <motion.div
      key="enter-mobile"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <button
        onClick={() => { setStep('landing'); setError(''); }}
        className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors tracking-widest uppercase mb-6"
      >
        <ArrowLeft size={12} /> BACK
      </button>

      <div className="mb-8">
        <p className="section-label mb-2">SECURE VERIFICATION</p>
        <h2 className="text-2xl font-bold text-white mb-2">PATIENT<br />IDENTIFICATION</h2>
        <p className="text-sm text-white/50 leading-relaxed">
          Enter your registered mobile number to receive a verification OTP.
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-xs text-white/50 tracking-widest uppercase mb-2" htmlFor="lab-mobile">
          REGISTERED MOBILE NUMBER <span className="text-primary">*</span>
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm font-medium">+91</span>
          <input
            id="lab-mobile"
            type="tel"
            value={mobile}
            onChange={(e) => { setMobile(e.target.value.replace(/\D/g, '').slice(0, 10)); setError(''); }}
            placeholder="10-digit mobile number"
            maxLength={10}
            className={`w-full pl-14 pr-4 py-4 bg-white/5 border rounded-xl text-base text-white placeholder:text-white/25 focus:outline-none focus:ring-1 transition-all ${
              error ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/20' : 'border-white/10 focus:border-primary/50 focus:ring-primary/20'
            }`}
          />
        </div>
        {error && (
          <p className="mt-2 text-xs text-red-400 flex items-center gap-1">
            <AlertCircle size={11} /> {error}
          </p>
        )}
      </div>

      <div className="mb-6 p-3 rounded-xl bg-amber-950/30 border border-amber-900/40 flex items-start gap-2">
        <Info size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-400/70 leading-relaxed">
          <span className="font-semibold text-amber-400">DEMO MODE:</span> Use mobile number <span className="font-mono">{DEMO_MOBILE_NUMBER}</span> to access demo reports.
        </p>
      </div>

      <button
        onClick={handleSendOtp}
        disabled={loading || mobile.length < 10}
        className="w-full flex items-center justify-center gap-2 btn-primary py-4 text-sm tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {loading ? 'SENDING OTP…' : 'SEND OTP'}
        {!loading && <ChevronRight size={16} />}
      </button>
    </motion.div>
  );

  // ── Enter OTP ────────────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otp.replace(/\s/g, '').length < 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setError('');
    setLoading(true);
    const normalised = mobile.replace(/\s/g, '').replace(/^(\+91|91)/, '');
    const result = await PathologyService.verifyPatient(normalised, otp);
    if (result.success && result.patient) {
      setPatient(result.patient);
      const reportsResult = await PathologyService.getReportsForPatient(result.patient.patientId);
      setLoading(false);
      if (reportsResult.success) {
        setReports(reportsResult.reports);
        setStep('reports-list');
      } else {
        setError(reportsResult.error || 'Could not retrieve reports.');
      }
    } else {
      setLoading(false);
      setError(result.error || 'Verification failed. Please check your OTP.');
    }
  };

  const EnterOtpStep = () => (
    <motion.div
      key="enter-otp"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <button
        onClick={() => { setStep('enter-mobile'); setOtp(''); setError(''); }}
        className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors tracking-widest uppercase mb-6"
      >
        <ArrowLeft size={12} /> BACK
      </button>

      <div className="mb-8">
        <p className="section-label mb-2">SECURE VERIFICATION</p>
        <h2 className="text-2xl font-bold text-white mb-2">ENTER YOUR OTP</h2>
        <p className="text-sm text-white/50 leading-relaxed">
          Enter the 6-digit OTP sent to <span className="text-white/80 font-medium">+91 {mobile}</span>
        </p>
      </div>

      <div className="mb-6">
        <OtpInput value={otp} onChange={(v) => { setOtp(v); setError(''); }} />
        {error && (
          <p className="mt-3 text-xs text-red-400 flex items-center justify-center gap-1">
            <AlertCircle size={11} /> {error}
          </p>
        )}
      </div>

      <div className="mb-6 p-3 rounded-xl bg-amber-950/30 border border-amber-900/40 flex items-start gap-2">
        <Info size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-400/70 leading-relaxed">
          <span className="font-semibold text-amber-400">DEMO OTP:</span> <span className="font-mono text-amber-300">{DEMO_OTP}</span>
        </p>
      </div>

      <button
        onClick={handleVerifyOtp}
        disabled={loading || otp.replace(/\s/g, '').length < 6}
        className="w-full flex items-center justify-center gap-2 btn-primary py-4 text-sm tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {loading ? 'VERIFYING…' : 'VERIFY & CONTINUE'}
        {!loading && <ChevronRight size={16} />}
      </button>
    </motion.div>
  );

  // ── Reports List ─────────────────────────────────────────────────────────
  const ReportsListStep = () => (
    <motion.div
      key="reports-list"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.4 }}
    >
      {/* Welcome */}
      <div className="mb-6">
        <p className="section-label mb-1">LAB REPORTS</p>
        <h2 className="text-2xl font-bold text-white mb-1">
          WELCOME, <span className="gold-gradient-text">{patient?.firstName?.toUpperCase()}.</span>
        </h2>
        <div className="flex items-center gap-2 mt-2">
          <User size={11} className="text-white/30" />
          <span className="text-xs text-white/30 font-mono">{patient?.patientId}</span>
          <DemoBadge />
        </div>
      </div>

      {/* Reports */}
      <p className="text-xs font-semibold tracking-widest text-white/30 uppercase mb-3">
        {reports.length} REPORT{reports.length !== 1 ? 'S' : ''} AVAILABLE
      </p>

      <div className="space-y-3 mb-6">
        {reports.map((report, i) => (
          <motion.button
            key={report.reportId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            whileHover={{ y: -1, borderColor: 'rgba(201,168,76,0.3)' }}
            onClick={() => { setSelectedReport(report); setStep('report-viewer'); }}
            className="w-full text-left p-4 rounded-2xl bg-card border border-white/8 transition-all duration-200 hover:bg-white/3"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <DemoBadge />
                  <StatusBadge status={report.status} />
                </div>
                <p className="text-sm font-bold text-white">{report.testName}</p>
                <p className="text-xs text-primary/60 mt-0.5">{report.category}</p>
              </div>
              <ChevronRight size={16} className="text-white/20 flex-shrink-0 mt-1" />
            </div>
            <div className="flex items-center gap-3 text-xs text-white/30">
              <span className="flex items-center gap-1"><Calendar size={10} />{report.testDate}</span>
              <span className="flex items-center gap-1"><MapPin size={10} />{report.centreName.split(',')[0]}</span>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Security note */}
      <div className="p-3 rounded-xl bg-white/3 border border-white/8 flex items-start gap-2">
        <Shield size={12} className="text-white/30 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-white/30 leading-relaxed">
          Only your reports are shown. In production, access requires secure authentication and patient ownership verification.
        </p>
      </div>
    </motion.div>
  );

  // ── Report Viewer ────────────────────────────────────────────────────────
  const handleRequestViaWhatsApp = async () => {
    if (!patient || !selectedReport) return;
    setLoading(true);
    const accessResult = await PathologyService.generateSecureReportAccess(
      patient.patientId,
      selectedReport.reportId
    );
    setLoading(false);
    const id = generateLabRequestId();
    setRequestId(id);
    PathologyService.logReportAccess({
      patientId: patient.patientId,
      reportId: selectedReport.reportId,
      action: 'REPORT_REQUESTED',
      timestamp: new Date().toISOString(),
      channel: 'WEBSITE',
    });
    setStep('whatsapp-simulation');
  };

  const ReportViewerStep = () => {
    if (!selectedReport || !patient) return null;
    return (
      <motion.div
        key="report-viewer"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        <button
          onClick={() => { setStep('reports-list'); setSelectedReport(null); }}
          className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors tracking-widest uppercase mb-6"
        >
          <ArrowLeft size={12} /> MY REPORTS
        </button>

        <div className="mb-6">
          <p className="section-label mb-1">LAB REPORT</p>
          <div className="flex items-center gap-2 mb-1">
            <DemoBadge />
            <span className="text-xs text-amber-400/60 font-semibold tracking-wider">DEMO REPORT — NOT A REAL MEDICAL REPORT</span>
          </div>
          <h2 className="text-2xl font-bold text-white mt-2">{selectedReport.testName}</h2>
        </div>

        {/* Report card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-2xl bg-card border border-white/10 mb-4"
          style={{ borderColor: 'rgba(201,168,76,0.15)' }}
        >
          {/* Card header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/8">
            <div>
              <p className="text-xs font-semibold tracking-widest text-primary/60 uppercase">ALL IS WELL</p>
              <p className="text-xs text-white/30 tracking-widest uppercase">LABORATORY REPORT</p>
            </div>
            <StatusBadge status={selectedReport.status} />
          </div>

          {/* Details */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <FileText size={13} className="text-white/30 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-white/30 uppercase tracking-wider mb-0.5">TEST</p>
                <p className="text-sm font-semibold text-white">{selectedReport.testName}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar size={13} className="text-white/30 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-white/30 uppercase tracking-wider mb-0.5">DATE</p>
                <p className="text-sm font-semibold text-white">{selectedReport.testDate}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Building2 size={13} className="text-white/30 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-white/30 uppercase tracking-wider mb-0.5">CENTRE</p>
                <p className="text-sm font-semibold text-white">{selectedReport.centreName}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User size={13} className="text-white/30 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-white/30 uppercase tracking-wider mb-0.5">PATIENT</p>
                <p className="text-sm font-semibold text-white">{patient.fullName}</p>
                <p className="text-xs text-white/30 font-mono">{patient.patientId}</p>
              </div>
            </div>
          </div>

          {/* Demo report content placeholder */}
          <div className="mt-4 p-4 rounded-xl bg-amber-950/20 border border-amber-900/30">
            <p className="text-xs font-bold text-amber-400 tracking-wider mb-1">DEMO — NOT A REAL MEDICAL REPORT</p>
            <p className="text-xs text-amber-400/60 leading-relaxed">
              In production, the actual lab results will be securely retrieved from the All Is Well Pathology/LIS system and delivered as a verified PDF report. No fabricated medical results are shown here.
            </p>
          </div>
        </motion.div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleRequestViaWhatsApp}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm tracking-widest uppercase transition-all duration-300 disabled:opacity-50"
            style={{ background: '#25D366', color: '#fff' }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <WhatsAppIcon size={18} />}
            {loading ? 'PREPARING…' : 'SEND VIA WHATSAPP'}
            {!loading && <ChevronRight size={16} />}
          </button>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                PathologyService.logReportAccess({
                  patientId: patient.patientId,
                  reportId: selectedReport.reportId,
                  action: 'REPORT_VIEWED',
                  timestamp: new Date().toISOString(),
                  channel: 'WEBSITE',
                });
              }}
              className="flex items-center justify-center gap-2 btn-outline-gold text-xs px-4 py-3"
            >
              <Eye size={13} />
              VIEW REPORT
            </button>
            <button
              onClick={() => {
                PathologyService.logReportAccess({
                  patientId: patient.patientId,
                  reportId: selectedReport.reportId,
                  action: 'REPORT_DOWNLOADED',
                  timestamp: new Date().toISOString(),
                  channel: 'WEBSITE',
                });
              }}
              className="flex items-center justify-center gap-2 btn-ghost-white text-xs px-4 py-3"
            >
              <Download size={13} />
              DOWNLOAD PDF
            </button>
          </div>
        </div>

        <p className="text-xs text-white/20 text-center mt-4 leading-relaxed">
          In production, VIEW and DOWNLOAD require secure authentication and generate expiring access tokens. Reports are never publicly accessible.
        </p>
      </motion.div>
    );
  };

  // ── WhatsApp Simulation ──────────────────────────────────────────────────
  const handleOpenWhatsApp = () => {
    if (!patient || !selectedReport) return;
    const message = buildReportWhatsAppMessage(patient, selectedReport);
    const url = `${hospitalConfig.officialWhatsAppLink}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    PathologyService.logReportAccess({
      patientId: patient.patientId,
      reportId: selectedReport!.reportId,
      action: 'REPORT_SENT_WHATSAPP',
      timestamp: new Date().toISOString(),
      channel: 'WHATSAPP',
    });
  };

  const WhatsAppSimulationStep = () => {
    if (!patient || !selectedReport) return null;
    return (
      <motion.div
        key="whatsapp-simulation"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.4 }}
      >
        {/* Success indicator */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          className="flex flex-col items-center text-center mb-8 pt-2"
        >
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
            style={{ background: 'rgba(37,211,102,0.12)', border: '1.5px solid rgba(37,211,102,0.3)' }}
          >
            <WhatsAppIcon size={36} />
          </div>
          <p className="text-xs font-semibold tracking-widest text-white/30 uppercase mb-2">WHATSAPP REPORT DELIVERY — DEMO</p>
          <h2 className="text-2xl font-bold text-white mb-2">REPORT READY</h2>
          <p className="text-sm text-white/50 leading-relaxed max-w-sm">
            Your lab report request has been prepared. Open WhatsApp to send it to the All Is Well team.
          </p>
        </motion.div>

        {/* Request ID */}
        <div className="mb-4 p-4 rounded-xl border border-primary/20 bg-primary/5">
          <p className="text-xs text-white/40 tracking-widest uppercase mb-1">REQUEST ID</p>
          <p className="text-base font-bold text-primary tracking-wider font-mono">{requestId}</p>
          <p className="text-xs text-white/30 mt-1">Keep this ID for your reference.</p>
        </div>

        {/* WhatsApp message preview */}
        <div className="mb-5 rounded-2xl overflow-hidden border border-white/10">
          <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: '#075E54' }}>
            <WhatsAppIcon size={14} />
            <p className="text-xs font-semibold text-white tracking-wider">WHATSAPP MESSAGE PREVIEW</p>
            <span className="ml-auto text-xs text-white/50 font-semibold tracking-wider">DEMO</span>
          </div>
          <div className="p-4 bg-white/3">
            <div className="p-3 rounded-xl bg-white/5 border border-white/8">
              <p className="text-xs text-white/70 leading-relaxed font-mono whitespace-pre-line">
                {buildReportWhatsAppMessage(patient, selectedReport)}
              </p>
            </div>
          </div>
        </div>

        {/* Important notice */}
        <div className="mb-5 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
          <p className="text-xs font-bold text-amber-400 tracking-wider mb-1.5">IMPORTANT</p>
          <p className="text-xs text-white/50 leading-relaxed">
            This is a prototype demonstration. In production, the All Is Well backend will verify your identity, retrieve your report from the secure pathology system, and deliver it through the official WhatsApp Business account.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleOpenWhatsApp}
            className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm tracking-widest uppercase transition-all duration-300"
            style={{ background: '#25D366', color: '#fff' }}
          >
            <WhatsAppIcon size={18} />
            OPEN WHATSAPP
            <ChevronRight size={16} />
          </button>
          <button
            onClick={handleOpenWhatsApp}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-white/10 bg-white/3 text-white/50 hover:text-white/80 hover:border-white/20 font-semibold text-xs tracking-widest uppercase transition-all duration-200"
          >
            <WhatsAppIcon size={14} />
            OPEN WHATSAPP AGAIN
          </button>
          <button
            onClick={() => router.push('/my-all-is-well')}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-white/8 bg-transparent text-white/30 hover:text-white/60 hover:border-white/15 font-semibold text-xs tracking-widest uppercase transition-all duration-200"
          >
            MY ALL IS WELL
            <ChevronRight size={14} />
          </button>
          <button
            onClick={() => router.push('/')}
            className="w-full flex items-center justify-center gap-2 py-3 text-white/20 hover:text-white/50 font-semibold text-xs tracking-widest uppercase transition-all duration-200"
          >
            BACK TO ALL IS WELL EXPERIENCE
          </button>
        </div>
      </motion.div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="journey-bg min-h-screen flex flex-col pb-24" role="main" aria-label="Get My Lab Report">
      <TopNav showBackButton backHref="/diagnostics" backLabel="Diagnostics" />

      <div className="flex-1 flex flex-col items-center pt-24 px-4 md:px-8">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            {step === 'landing' && <LandingStep key="landing" />}
            {step === 'enter-mobile' && <EnterMobileStep key="enter-mobile" />}
            {step === 'enter-otp' && <EnterOtpStep key="enter-otp" />}
            {step === 'reports-list' && <ReportsListStep key="reports-list" />}
            {step === 'report-viewer' && <ReportViewerStep key="report-viewer" />}
            {step === 'whatsapp-simulation' && <WhatsAppSimulationStep key="whatsapp-simulation" />}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}
